import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMessage } from 'components/ui';
import {
  type AtlasGqlHorizonDamage,
  AtlasGqlConfirmationStatus,
  useSetHorizonDamageConfirmationStatusesMutation,
} from 'types/atlas-graphql';
import { useApolloContext } from 'utils/apollo';

// `ConfirmedUpdated` counts as unconfirmed: it was confirmed once, but new data
// landed on it after that.
export const getUnconfirmedDamages = (damages: AtlasGqlHorizonDamage[]): AtlasGqlHorizonDamage[] =>
  damages.filter((damage) => damage.confirmationStatus !== AtlasGqlConfirmationStatus.Confirmed);

type BulkConfirmResult = {
  confirmedIds: string[];
  failedCount: number;
};

export const useBulkConfirmDamages = (refetchQueries?: string[]) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [confirming, setConfirming] = useState(false);
  const [setConfirmationStatuses] = useSetHorizonDamageConfirmationStatusesMutation();
  const { atlasClient } = useApolloContext();

  const confirm = async (damages: AtlasGqlHorizonDamage[]): Promise<BulkConfirmResult> => {
    const unconfirmed = getUnconfirmedDamages(damages);
    if (unconfirmed.length === 0) {
      return { confirmedIds: [], failedCount: 0 };
    }

    setConfirming(true);

    let confirmedIds: string[] = [];
    try {
      const { data } = await setConfirmationStatuses({
        variables: {
          input: {
            ids: unconfirmed.map((damage) => damage.id),
            confirmationStatus: AtlasGqlConfirmationStatus.Confirmed,
          },
        },
      });
      // The mutation skips Damages that were deleted or belong to another
      // organization, so the response — not the request — says what confirmed.
      confirmedIds = (data?.setHorizonDamageConfirmationStatuses ?? []).map((damage) =>
        String(damage.id)
      );
    } catch {
      // The Apollo error link reports to Bugsnag but shows the user nothing, so the
      // failure message below is the only one they see.
    }

    const failedCount = unconfirmed.length - confirmedIds.length;

    if (confirmedIds.length > 0) {
      message.success(
        t('damage.damageConfirmedCount', '{{count}} Damages confirmed.', {
          count: confirmedIds.length,
        })
      );
    }

    // One refetch for the whole batch, so a row just confirmed drops out of a
    // confirmation-filtered view. A failed refetch only leaves the view stale — the
    // confirmation already landed, so it must not lose the result the caller uses to
    // clear its selection, nor strand the confirming state.
    if (confirmedIds.length > 0 && refetchQueries?.length) {
      try {
        await atlasClient.refetchQueries({ include: refetchQueries });
      } catch {
        // The Apollo error link reports to Bugsnag.
      }
    }

    if (failedCount > 0) {
      message.error(
        t('damage.unableToConfirmCount', 'Unable to confirm {{count}} selected Damages.', {
          count: failedCount,
        })
      );
    }

    setConfirming(false);
    return { confirmedIds, failedCount };
  };

  return { confirm, confirming };
};
